import { useState, useEffect, useMemo, useRef } from "react";
import { Loader2, ChevronDown } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// FIN-4 — InvestimentoModal (Add | Edit).
//
// 8 campos + preview live (useMemo recalcula em tempo real). Validação
// client-side ANTES de POST/PUT.

const TYPE_OPTIONS = [
  { value: "acoes",              label: "Ações" },
  { value: "FII",                label: "FII" },
  { value: "fundo_acoes",        label: "Fundo de Ações" },
  { value: "fundo_multimercado", label: "Fundo Multimercado" },
  { value: "CDB",                label: "CDB" },
  { value: "LCI",                label: "LCI" },
  { value: "LCA",                label: "LCA" },
  { value: "tesouro",            label: "Tesouro" },
  { value: "cripto",             label: "Cripto" },
  { value: "outros",             label: "Outros" },
];

function _toCents(v) {
  if (v == null || v === "") return null;
  const s = String(v).replace(/\./g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function _toQty(v) {
  if (v == null || v === "") return null;
  const s = String(v).replace(/\./g, "").replace(",", ".").replace(/[^0-9.\-]/g, "");
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function _formatBRL(v) {
  if (v == null) return "";
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function _formatQty(v) {
  if (v == null) return "";
  return Number(v).toLocaleString("pt-BR", { minimumFractionDigits: 0, maximumFractionDigits: 8 });
}

const fmtBRL = (v) => Number(v ?? 0).toLocaleString("pt-BR", {
  style: "currency", currency: "BRL",
});

function ValidationMsg({ children }) {
  return <p className="text-micro text-status-error mt-1" role="alert">{children}</p>;
}

export default function InvestimentoModal({
  open,
  onOpenChange,
  investment,            // null = modo create, obj = modo edit
  onCreate,              // (payload) => Promise<inv>
  onUpdate,              // (id, patch) => Promise<inv>
  knownInstitutions = [],
}) {
  const isEditing = Boolean(investment);

  const [name, setName] = useState("");
  const [type, setType] = useState("acoes");
  const [ticker, setTicker] = useState("");
  const [quantity, setQuantity] = useState("");
  const [purchasePrice, setPurchasePrice] = useState("");
  const [currentValue, setCurrentValue] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");
  const [institution, setInstitution] = useState("");
  const [notes, setNotes] = useState("");
  const [showInstSuggestions, setShowInstSuggestions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState({});
  const instInputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(investment?.name || "");
      setType(investment?.type || "acoes");
      setTicker(investment?.ticker || "");
      setQuantity(investment?.quantity != null ? _formatQty(investment.quantity) : "");
      setPurchasePrice(investment?.purchase_price != null ? _formatBRL(investment.purchase_price) : "");
      setCurrentValue(investment?.current_value != null ? _formatBRL(investment.current_value) : "");
      setPurchaseDate(
        investment?.purchase_date
          ? String(investment.purchase_date).slice(0, 10)
          : new Date().toISOString().slice(0, 10),
      );
      setInstitution(investment?.institution || "");
      setNotes(investment?.notes || "");
      setSaving(false);
      setTouched({});
      setShowInstSuggestions(false);
    }
  }, [open, investment]);

  // Validações
  const errors = useMemo(() => {
    const out = {};
    const nameTrim = (name || "").trim();
    if (!nameTrim) out.name = "Informe um nome";
    else if (nameTrim.length > 100) out.name = "Máx 100 caracteres";

    const qty = _toQty(quantity);
    if (qty == null || qty <= 0) out.quantity = "Quantidade > 0";

    const pp = _toCents(purchasePrice);
    if (pp == null || pp <= 0) out.purchasePrice = "Preço > 0";

    const cv = _toCents(currentValue);
    if (cv == null || cv < 0) out.currentValue = "Valor ≥ 0";

    if (!purchaseDate) out.purchaseDate = "Informe a data";
    else {
      try {
        const d = new Date(purchaseDate + "T00:00:00");
        if (d > new Date()) out.purchaseDate = "Data não pode ser futura";
      } catch { out.purchaseDate = "Data inválida"; }
    }

    const inst = (institution || "").trim();
    if (!inst) out.institution = "Informe a instituição";
    else if (inst.length > 50) out.institution = "Máx 50 caracteres";

    if (notes && notes.length > 500) out.notes = "Máx 500 caracteres";

    return out;
  }, [name, quantity, purchasePrice, currentValue, purchaseDate, institution, notes]);

  const hasErrors = Object.keys(errors).length > 0;
  const showErr = (k) => touched[k] && errors[k];

  // Preview live
  const preview = useMemo(() => {
    const qty = _toQty(quantity) || 0;
    const pp = _toCents(purchasePrice) || 0;
    const cv = _toCents(currentValue) || 0;
    const purchaseTotal = qty * pp;
    const currentTotal = qty * cv;
    const gainLoss = currentTotal - purchaseTotal;
    const gainPct = purchaseTotal > 0 ? (gainLoss / purchaseTotal) * 100 : 0;
    return { purchaseTotal, currentTotal, gainLoss, gainPct };
  }, [quantity, purchasePrice, currentValue]);

  const filteredInstitutions = useMemo(() => {
    if (!institution || !showInstSuggestions) return [];
    const q = institution.toLowerCase();
    return knownInstitutions.filter(s => s.toLowerCase().includes(q) && s.toLowerCase() !== q).slice(0, 5);
  }, [institution, showInstSuggestions, knownInstitutions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({
      name: true, quantity: true, purchasePrice: true, currentValue: true,
      purchaseDate: true, institution: true, notes: true,
    });
    if (hasErrors) return;
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        type,
        ticker: ticker.trim() || null,
        quantity: _toQty(quantity),
        purchase_price: _toCents(purchasePrice),
        current_value: _toCents(currentValue),
        purchase_date: new Date(purchaseDate + "T00:00:00").toISOString(),
        institution: institution.trim(),
        notes: notes.trim() || null,
      };
      if (isEditing) {
        await onUpdate(investment.id, payload);
        toast.success("Investimento atualizado");
      } else {
        await onCreate(payload);
        toast.success("Investimento adicionado");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(err.message || "Falha ao salvar. Tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface-overlay border border-subtle max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="invest-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="invest-modal-title">
            {isEditing ? `Editar ${investment?.name || ""}` : "Adicionar investimento"}
          </DialogTitle>
          <DialogDescription className="text-foreground-muted text-caption">
            Dados manuais. Você atualiza o "valor atual" quando quiser revisar a posição.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-3.5 py-2">
            {/* Nome */}
            <div>
              <label htmlFor="inv-name" className="block text-caption font-medium text-foreground-muted mb-1">
                Nome
              </label>
              <Input
                id="inv-name"
                type="text"
                autoFocus
                value={name}
                onChange={(e) => setName(e.target.value)}
                onBlur={() => setTouched(t => ({ ...t, name: true }))}
                placeholder="Ex: PETR4 - Petrobras"
                maxLength={120}
                aria-invalid={Boolean(showErr("name"))}
                data-testid="invest-modal-name"
              />
              {showErr("name") && <ValidationMsg>{errors.name}</ValidationMsg>}
            </div>

            {/* Tipo + Ticker grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="inv-type" className="block text-caption font-medium text-foreground-muted mb-1">
                  Tipo
                </label>
                <div className="relative">
                  <select
                    id="inv-type"
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full appearance-none px-3 py-2 pr-8 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40"
                    data-testid="invest-modal-type"
                  >
                    {TYPE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-foreground-muted pointer-events-none" />
                </div>
              </div>
              <div>
                <label htmlFor="inv-ticker" className="block text-caption font-medium text-foreground-muted mb-1">
                  Ticker (opcional)
                </label>
                <Input
                  id="inv-ticker"
                  type="text"
                  value={ticker}
                  onChange={(e) => setTicker(e.target.value.toUpperCase())}
                  placeholder="PETR4, BTC"
                  maxLength={20}
                  className="uppercase"
                  data-testid="invest-modal-ticker"
                />
              </div>
            </div>

            {/* Qtd + Data grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="inv-qty" className="block text-caption font-medium text-foreground-muted mb-1">
                  Quantidade
                </label>
                <Input
                  id="inv-qty"
                  type="text"
                  inputMode="decimal"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, quantity: true }))}
                  placeholder="100"
                  aria-invalid={Boolean(showErr("quantity"))}
                  data-testid="invest-modal-qty"
                />
                {showErr("quantity") && <ValidationMsg>{errors.quantity}</ValidationMsg>}
              </div>
              <div>
                <label htmlFor="inv-date" className="block text-caption font-medium text-foreground-muted mb-1">
                  Data de compra
                </label>
                <Input
                  id="inv-date"
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, purchaseDate: true }))}
                  max={new Date().toISOString().slice(0, 10)}
                  aria-invalid={Boolean(showErr("purchaseDate"))}
                  data-testid="invest-modal-date"
                />
                {showErr("purchaseDate") && <ValidationMsg>{errors.purchaseDate}</ValidationMsg>}
              </div>
            </div>

            {/* Preço + Valor atual grid */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="inv-price" className="block text-caption font-medium text-foreground-muted mb-1">
                  Preço de compra (R$)
                </label>
                <Input
                  id="inv-price"
                  type="text"
                  inputMode="decimal"
                  value={purchasePrice}
                  onChange={(e) => setPurchasePrice(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, purchasePrice: true }))}
                  placeholder="32,50"
                  aria-invalid={Boolean(showErr("purchasePrice"))}
                  data-testid="invest-modal-price"
                />
                {showErr("purchasePrice") && <ValidationMsg>{errors.purchasePrice}</ValidationMsg>}
              </div>
              <div>
                <label htmlFor="inv-current" className="block text-caption font-medium text-foreground-muted mb-1">
                  Valor atual unit. (R$)
                </label>
                <Input
                  id="inv-current"
                  type="text"
                  inputMode="decimal"
                  value={currentValue}
                  onChange={(e) => setCurrentValue(e.target.value)}
                  onBlur={() => setTouched(t => ({ ...t, currentValue: true }))}
                  placeholder="38,20"
                  aria-invalid={Boolean(showErr("currentValue"))}
                  data-testid="invest-modal-current"
                />
                {showErr("currentValue") && <ValidationMsg>{errors.currentValue}</ValidationMsg>}
              </div>
            </div>

            {/* Instituição com autocomplete */}
            <div className="relative">
              <label htmlFor="inv-inst" className="block text-caption font-medium text-foreground-muted mb-1">
                Instituição
              </label>
              <Input
                id="inv-inst"
                ref={instInputRef}
                type="text"
                value={institution}
                onChange={(e) => { setInstitution(e.target.value); setShowInstSuggestions(true); }}
                onFocus={() => setShowInstSuggestions(true)}
                onBlur={() => {
                  setTouched(t => ({ ...t, institution: true }));
                  // Atrasa fechar pra permitir click na suggestion
                  setTimeout(() => setShowInstSuggestions(false), 150);
                }}
                placeholder="Ex: XP, Inter, Mercado Bitcoin"
                maxLength={50}
                autoComplete="off"
                aria-invalid={Boolean(showErr("institution"))}
                data-testid="invest-modal-institution"
              />
              {showErr("institution") && <ValidationMsg>{errors.institution}</ValidationMsg>}
              {filteredInstitutions.length > 0 && (
                <div
                  className="absolute z-10 left-0 right-0 mt-0.5 bg-surface-overlay border border-subtle rounded-md shadow-lg overflow-hidden"
                  role="listbox"
                >
                  {filteredInstitutions.map(s => (
                    <button
                      key={s}
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); setInstitution(s); setShowInstSuggestions(false); }}
                      className="w-full text-left px-3 py-1.5 text-caption text-foreground-muted hover:bg-surface-card-hover hover:text-foreground"
                      data-testid={`invest-modal-inst-suggestion-${s.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Notas */}
            <div>
              <label htmlFor="inv-notes" className="block text-caption font-medium text-foreground-muted mb-1">
                Notas (opcional)
              </label>
              <textarea
                id="inv-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={2}
                maxLength={500}
                placeholder="Observações sobre essa posição..."
                className="w-full px-3 py-2 bg-surface-card-hover border border-subtle rounded-md text-foreground text-caption focus:outline-none focus:ring-1 focus:ring-brand-cyan focus:border-brand-cyan/40 resize-none"
                data-testid="invest-modal-notes"
              />
              {showErr("notes") && <ValidationMsg>{errors.notes}</ValidationMsg>}
            </div>

            {/* Preview live */}
            <div className="bg-surface-card-hover/40 border border-subtle rounded-md p-3 space-y-1.5" data-testid="invest-modal-preview">
              <p className="text-micro text-foreground-muted uppercase font-semibold mb-1.5" style={{ letterSpacing: "0.10em" }}>
                Preview
              </p>
              <div className="flex items-center justify-between text-caption">
                <span className="text-foreground-muted">Total investido</span>
                <span className="font-mono tabular-nums text-foreground">{fmtBRL(preview.purchaseTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-caption">
                <span className="text-foreground-muted">Valor atual</span>
                <span className="font-mono tabular-nums text-foreground">{fmtBRL(preview.currentTotal)}</span>
              </div>
              <div className="flex items-center justify-between text-caption pt-1.5 border-t border-subtle/40">
                <span className="text-foreground-muted">Ganho/Perda</span>
                <span className={cn(
                  "font-mono tabular-nums font-semibold",
                  preview.gainLoss > 0 ? "text-brand-green" :
                  preview.gainLoss < 0 ? "text-status-error" :
                  "text-foreground-muted",
                )}>
                  {preview.gainLoss === 0 ? fmtBRL(0) :
                    `${preview.gainLoss > 0 ? "+" : "−"}${fmtBRL(Math.abs(preview.gainLoss))} (${preview.gainPct > 0 ? "+" : "−"}${Math.abs(preview.gainPct).toLocaleString("pt-BR", { maximumFractionDigits: 2 })}%)`}
                </span>
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              data-testid="invest-modal-cancel"
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving || hasErrors}
              data-testid="invest-modal-submit"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {isEditing ? "Salvar" : "Adicionar"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
